import { apiRequest } from './client';

/**
 * Clients for Meeting Rooms (FEAT-007) and Room Booking (FEAT-006).
 *
 * One file, because every booking screen needs the bookable-room list and
 * splitting them would mean one importing the other.
 */

export type RoomStatus = 'available' | 'maintenance' | 'closed';

export interface Room {
  id: string;
  name: string;
  capacity: number;
  status: RoomStatus;
  allowed_company_ids: string[];
  allowed_employee_ids: string[];
  /** Whether *this* caller may reserve it. Computed server-side. */
  can_book: boolean;
  upcoming_bookings: number;
  /**
   * FEAT-018 Stage 4 — the room's own resource calendar, or null.
   *
   * Not a credential, so it is returned plainly: it is a directory address an
   * administrator can already read in Workspace or 365, and masking it would
   * make "is this room wired up?" unanswerable from the screen that wires it up.
   */
  resource_email_google: string | null;
  resource_email_microsoft: string | null;
  resource_email: string | null;
  resource_provider: CalendarProvider | null;
  /**
   * FEAT-018 Stage 7 — accept bookings made outside Boud on this room.
   *
   * Off by default and turned on one room at a time. It never cancels anything:
   * a booking is only ever cancelled by a person acting in Boud.
   */
  inbound_enabled: boolean;
}

/** Which platform a room's resource calendar lives on. */
export type CalendarProvider = 'google' | 'microsoft';

export interface RoomImpact {
  id: string;
  name: string;
  upcoming_bookings: number;
}

export interface BookingInvitee {
  employee_id: string;
  name: string;
  status: 'invited' | 'accepted' | 'declined';
}

export interface Booking {
  id: string;
  room_id: string;
  room_name: string;
  title: string;
  starts_at: string;
  ends_at: string;
  /** The Riyadh calendar date, derived server-side so both sides agree. */
  date: string;
  organizer_id: string;
  organizer_name: string;
  /** Only for a booking made outside Boud. */
  organizer_email: string | null;
  /** From their account, not typed on the form. Null for an outside organiser. */
  organizer_company: string | null;
  invitees: BookingInvitee[];
  /** UX only — the server enforces the same rule (REQ-040). */
  can_manage: boolean;
}

export interface RoomInput {
  name?: string;
  capacity?: number;
  status?: RoomStatus;
  allowed_company_ids?: string[];
  allowed_employee_ids?: string[];
  /**
   * Absent leaves the room's calendar alone; `null` disconnects it. The two are
   * different requests, which is why this is optional *and* nullable.
   */
  resource_email_google?: string | null;
  resource_email_microsoft?: string | null;
  inbound_enabled?: boolean;
}

/**
 * FEAT-018 Stage 5 — the result of reading a room's resource calendar.
 *
 * `failure` is a code rather than a sentence so the client localises it; `detail`
 * is the provider's own words, shown to an administrator underneath.
 */
export interface RoomCalendarCheck {
  room_id: string;
  room_name: string;
  resource_email: string | null;
  resource_provider: CalendarProvider | null;
  ok: boolean;
  failure:
    'no_resource' | 'no_connection' | 'provider_mismatch' | 'missing_scope' | 'unreachable' | null;
  detail: string | null;
  events_found: number | null;
  events_from_boud: number | null;
}

/** Read-only. Safe to press repeatedly while fixing the resource at the far end. */
export function checkRoomCalendar(id: string): Promise<RoomCalendarCheck> {
  return apiRequest<RoomCalendarCheck>(`/rooms/${id}/calendar-check`);
}

export function listRooms(): Promise<Room[]> {
  return apiRequest<Room[]>('/rooms');
}

export function listBookableRooms(): Promise<Room[]> {
  return apiRequest<Room[]>('/rooms/bookable');
}

export function createRoom(body: RoomInput): Promise<Room> {
  return apiRequest<Room>('/rooms', { method: 'POST', body });
}

export function updateRoom(id: string, body: RoomInput): Promise<Room> {
  return apiRequest<Room>(`/rooms/${id}`, { method: 'PATCH', body });
}

export function roomImpact(id: string): Promise<RoomImpact> {
  return apiRequest<RoomImpact>(`/rooms/${id}/impact`);
}

export function deleteRoom(id: string): Promise<void> {
  return apiRequest<void>(`/rooms/${id}`, { method: 'DELETE' });
}

/**
 * REQ-107 — one range endpoint serves all three calendar views.
 *
 * Dates are Riyadh calendar dates, `YYYY-MM-DD`. The month view asks for the
 * whole visible grid including the leading and trailing days from the
 * neighbouring months, which is why the range is not simply the month.
 */
export function listBookings(params: {
  from: string;
  to: string;
  roomId?: string;
}): Promise<Booking[]> {
  return apiRequest<Booking[]>('/bookings', {
    query: { from: params.from, to: params.to, room_id: params.roomId },
  });
}

export interface BookingInput {
  room_id?: string;
  title?: string;
  starts_at?: string;
  ends_at?: string;
  invitee_ids?: string[];
}

/**
 * One booking in full.
 *
 * The Dashboard's upcoming list carries only enough to render a row — title,
 * room, times. Opening one asks for the whole record rather than widening that
 * payload, because the organiser, their company and the invitee list are only
 * wanted when somebody actually looks.
 */
export function getBooking(id: string): Promise<Booking> {
  return apiRequest<Booking>(`/bookings/${id}`);
}

export function createBooking(body: BookingInput): Promise<Booking> {
  return apiRequest<Booking>('/bookings', { method: 'POST', body });
}

export function updateBooking(id: string, body: BookingInput): Promise<Booking> {
  return apiRequest<Booking>(`/bookings/${id}`, { method: 'PATCH', body });
}

export function cancelBooking(id: string): Promise<void> {
  return apiRequest<void>(`/bookings/${id}`, { method: 'DELETE' });
}

export function respondToBooking(id: string, response: 'accepted' | 'declined'): Promise<Booking> {
  return apiRequest<Booking>(`/bookings/${id}/respond`, {
    method: 'POST',
    body: { response },
  });
}
